import { BadgeCheck, Building2, Bot, Code } from 'lucide-react';

interface AgentAvatarProps {
  name: string;
  avatarUrl: string | null;
  verificationStatus: string;
  accountType: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function AgentAvatar({
  name,
  avatarUrl,
  verificationStatus,
  accountType,
  size = 'md',
}: AgentAvatarProps) {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-14 h-14',
    xl: 'w-20 h-20',
  };

  const innerSizes = {
    sm: 'w-[26px] h-[26px]',
    md: 'w-[34px] h-[34px]',
    lg: 'w-[48px] h-[48px]',
    xl: 'w-[70px] h-[70px]',
  };

  const badgeSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
    xl: 'w-5 h-5',
  };

  const badgePositions = {
    sm: '-bottom-0.5 -right-0.5',
    md: '-bottom-0.5 -right-0.5',
    lg: '-bottom-0.5 -right-0.5',
    xl: '-bottom-1 -right-1',
  };

  // Generate a gradient pair from the agent's name for fallback avatar
  const getGradient = (name: string) => {
    const gradients = [
      'from-rose-500 to-orange-400',
      'from-amber-400 to-yellow-300',
      'from-emerald-400 to-teal-500',
      'from-cyan-400 to-blue-500',
      'from-violet-500 to-purple-400',
      'from-fuchsia-500 to-pink-400',
      'from-sky-400 to-indigo-500',
      'from-lime-400 to-green-500',
      'from-orange-400 to-red-500',
      'from-teal-400 to-cyan-300',
      'from-indigo-400 to-violet-500',
      'from-pink-400 to-rose-500',
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradients[index % gradients.length];
  };

  const initials = name.slice(0, 2).toUpperCase();
  const gradient = getGradient(name);

  const showBadge =
    verificationStatus === 'verified' ||
    verificationStatus === 'business_verified' ||
    accountType === 'bot' ||
    accountType === 'developer';

  const getBadgeIcon = () => {
    if (accountType === 'bot') return Bot;
    if (accountType === 'developer') return Code;
    if (verificationStatus === 'business_verified') return Building2;
    if (verificationStatus === 'verified') return BadgeCheck;
    return null;
  };

  const getBadgeColor = () => {
    if (accountType === 'bot') return 'text-muted';
    if (accountType === 'developer') return 'text-success';
    if (verificationStatus === 'business_verified') return 'text-warning';
    return 'text-neon';
  };

  const BadgeIcon = getBadgeIcon();

  return (
    <div className="relative inline-block flex-shrink-0">
      {/* Hexagonal outer frame */}
      <div className={`${sizes[size]} hex-clip bg-gradient-to-br from-border-bright to-border flex items-center justify-center`}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={`@${name}`}
            className={`${innerSizes[size]} hex-clip object-cover`}
          />
        ) : (
          <div
            className={`${innerSizes[size]} hex-clip bg-gradient-to-br ${gradient} flex items-center justify-center`}
          >
            <span
              className="text-white font-display font-bold"
              style={{ fontSize: size === 'xl' ? '1.5rem' : size === 'lg' ? '1.1rem' : size === 'md' ? '0.7rem' : '0.55rem' }}
            >
              {initials}
            </span>
          </div>
        )}
      </div>

      {showBadge && BadgeIcon && (
        <div
          className={`absolute ${badgePositions[size]} bg-void rounded-full p-0.5`}
        >
          <BadgeIcon className={`${badgeSizes[size]} ${getBadgeColor()}`} />
        </div>
      )}
    </div>
  );
}
